import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ChevronRight, X } from "lucide-react";
import { CategoryIcon } from "@/components/commerce/CategoryIcon";
import { Skeleton } from "@/components/ui";
import { useFocusTrap, useScrollLock } from "@/components/ui/focus";
import { cn } from "@/lib/cn";
import { useCategoryMenu } from "./api";

/**
 * Full-screen category browser (like the Jumia app): main categories on the left, the chosen
 * category's subcategories on the right. Esc or the close button returns to the page.
 */
export function CategoryBrowser({ open, onClose }: { open: boolean; onClose: () => void }) {
  const menu = useCategoryMenu();
  const roots = menu.data ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const right = useRef<HTMLDivElement>(null);
  const id = useId();
  useFocusTrap(ref, open);
  useScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  const current = roots.find((r) => r.slug === selected) ?? roots[0];
  useEffect(() => {
    if (right.current) right.current.scrollTop = 0;
  }, [current?.slug]);
  if (!open) return null;

  return createPortal(
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${id}-title`}
      tabIndex={-1}
      data-testid="category-browser"
      className="fixed inset-0 z-[75] flex animate-fade-in flex-col bg-void pt-safe-t"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-3">
        <h2 id={`${id}-title`} className="text-lg font-semibold">
          Categories
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="grid size-10 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
          aria-label="Close categories"
        >
          <X className="size-5" aria-hidden />
        </button>
      </div>
      <div className="flex min-h-0 flex-1">
        <div
          className="w-[104px] shrink-0 overflow-y-auto border-r border-line bg-surface/60 sm:w-40"
          role="tablist"
          aria-orientation="vertical"
          aria-label="Main categories"
        >
          {menu.isLoading
            ? Array.from({ length: 10 }, (_, i) => <Skeleton key={i} className="m-2 h-16" />)
            : roots.map((r) => {
                const on = r.slug === current?.slug;
                return (
                  <button
                    key={r.slug}
                    type="button"
                    role="tab"
                    aria-selected={on}
                    aria-controls={`${id}-panel`}
                    onClick={() => setSelected(r.slug)}
                    className={cn(
                      "relative flex w-full flex-col items-center gap-1 px-2 py-3 text-center text-[11px] leading-tight transition sm:flex-row sm:text-left sm:text-sm",
                      on ? "bg-elevated text-ink" : "text-ink-muted hover:text-ink",
                    )}
                  >
                    {on && (
                      <span
                        className="absolute inset-y-2 left-0 w-[3px] rounded-r-full"
                        style={{ background: r.accent_color || "var(--accent-cyan)" }}
                        aria-hidden
                      />
                    )}
                    <span style={{ color: r.accent_color || undefined }}>
                      <CategoryIcon name={r.icon} className="size-5" />
                    </span>
                    <span className="line-clamp-2">{r.name}</span>
                  </button>
                );
              })}
        </div>
        <div
          ref={right}
          id={`${id}-panel`}
          role="tabpanel"
          className="min-w-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-[calc(24px+var(--safe-bottom))] pt-4"
        >
          {current && (
            <>
              <Link
                to={`/c/${current.slug}`}
                onClick={onClose}
                className="mb-4 flex items-center justify-between rounded-card border border-fg/[0.06] bg-surface px-4 py-3 text-sm font-semibold"
              >
                <span>
                  See all {current.name}
                  <span className="tabular ml-1 font-normal text-ink-muted">({current.count.toLocaleString("en-NG")})</span>
                </span>
                <ChevronRight className="size-4 text-ink-muted" aria-hidden />
              </Link>
              {current.children.map((branch) => (
                <section key={branch.slug} className="mb-5">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">{branch.name}</h3>
                    <Link to={`/c/${branch.slug}`} onClick={onClose} className="text-xs font-medium text-[var(--primary-text)]">
                      See all
                    </Link>
                  </div>
                  <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {(branch.children.length ? branch.children : [branch]).map((leaf) => (
                      <li key={leaf.slug}>
                        <Link
                          to={`/c/${leaf.slug}`}
                          onClick={onClose}
                          className="flex h-full min-h-[72px] flex-col items-center justify-center gap-1.5 rounded-xl border border-fg/[0.05] bg-surface p-2 text-center text-[11px] leading-tight text-ink-soft hover:border-[var(--border-glow)] hover:text-ink"
                        >
                          <span style={{ color: current.accent_color || undefined }} className="text-cyan">
                            <CategoryIcon name={leaf.icon || current.icon} className="size-5" />
                          </span>
                          <span className="line-clamp-2">{leaf.name}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
